import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Grid,
  Alert,
  Autocomplete,
  Chip,
  InputAdornment
} from '@mui/material';
import { Transporter } from '@otw/models';

interface CreateTransporterDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (transporterData: {
    name: string;
    driver_ids: string[];
    admin_ids: string[];
    owner_id: string;
    price_per_mile?: number;
  }) => Promise<void>;
  transporters: Transporter[];
}

const CreateTransporterDialog: React.FC<CreateTransporterDialogProps> = ({
  open,
  onClose,
  onSubmit,
  transporters
}) => {
  const [name, setName] = useState('');
  const [selectedDrivers, setSelectedDrivers] = useState<Transporter[]>([]);
  const [selectedAdmins, setSelectedAdmins] = useState<Transporter[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<Transporter | null>(null);
  const [pricePerMile, setPricePerMile] = useState<number | ''>('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    drivers?: string;
    admins?: string;
    owner?: string;
    pricePerMile?: string;
  }>({});

  // Filter transporters by role
  const driverTransporters = transporters.filter(t => t.transporter_role === 'driver');
  const adminTransporters = transporters.filter(t => t.transporter_role === 'admin');
  const ownerTransporters = transporters.filter(t => t.transporter_role === 'owner');

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);


  const resetForm = () => {
    setName('');
    setSelectedDrivers([]);
    setSelectedAdmins([]);
    setSelectedOwner(null);
    setPricePerMile('');
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: { name?: string; drivers?: string; admins?: string; owner?: string; pricePerMile?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = 'Please enter a company name';
    }

    if (!selectedOwner) {
      newErrors.owner = 'Please select an owner for this transporter company';
    }

    if (pricePerMile === '' || pricePerMile === null || pricePerMile === undefined) {
      newErrors.pricePerMile = 'Price per mile is required';
    } else if (isNaN(Number(pricePerMile)) || Number(pricePerMile) < 0) {
      newErrors.pricePerMile = 'Price per mile must be a valid positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        driver_ids: selectedDrivers.map(driver => driver.id),
        admin_ids: selectedAdmins.map(admin => admin.id),
        owner_id: selectedOwner!.id,
        price_per_mile: pricePerMile !== '' ? Number(pricePerMile) : undefined
      });
      
      // Reset form on successful submission
      resetForm();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Transporter Group</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Company Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name || "Enter a name for this transporter company"}
          />
          
          <TextField
            label="Price per Mile"
            type="number"
            fullWidth
            value={pricePerMile}
            onChange={(e) => setPricePerMile(e.target.value === '' ? '' : Number(e.target.value))}
            error={!!errors.pricePerMile}
            helperText={errors.pricePerMile || "Enter the price per mile for this company (e.g., 2.50)"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: 'text.secondary' }}>$</Typography>
                </InputAdornment>
              ),
            }}
            inputProps={{
              min: 0,
              step: 0.01,
              placeholder: "0.00"
            }}
          />
          
          <FormControl fullWidth error={!!errors.owner}>
            <Autocomplete
              id="owner-select"
              options={ownerTransporters}
              getOptionLabel={(option) => `${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
              value={selectedOwner}
              onChange={(_, newValue) => setSelectedOwner(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Company Owner"
                  error={!!errors.owner}
                  helperText={errors.owner || "Designate a primary owner for this transporter company"}
                />
              )}
            />
          </FormControl>

          <Divider sx={{ my: 2 }} />
          
          <FormControl fullWidth error={!!errors.drivers}>
            <Autocomplete
              multiple
              id="drivers-select"
              options={driverTransporters}
              getOptionLabel={(option) => `${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
              value={selectedDrivers}
              onChange={(_, newValue) => setSelectedDrivers(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={`${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Drivers"
                  error={!!errors.drivers}
                  helperText={errors.drivers || "Add drivers to this group (optional)"}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth error={!!errors.admins}>
            <Autocomplete
              multiple
              id="admins-select"
              options={adminTransporters}
              getOptionLabel={(option) => `${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
              value={selectedAdmins}
              onChange={(_, newValue) => setSelectedAdmins(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={`${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Admins"
                  error={!!errors.admins}
                  helperText={errors.admins || "Add admins to this group (optional)"}
                />
              )}
            />
          </FormControl>
          
          {(selectedDrivers.length > 0 || selectedAdmins.length > 0) && (
            <Alert severity="info">
              This will create a transporter company with the selected drivers and admins. All members in the group will be managed by the designated owner.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting || !name.trim() || !selectedOwner || pricePerMile === ''}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting ? 'Creating...' : 'Create Transporter Group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTransporterDialog; 